import React, {useEffect, useRef, useState} from "react";
import {
    Button,
    Field,
    Input,
    makeStyles,
    Select,
    SelectProps,
    Textarea,
    tokens,
    Tree,
    TreeItem,
    TreeItemLayout
} from "@fluentui/react-components";
import {CalligraphyPenCheckmark20Regular} from "@fluentui/react-icons";
import {insertText} from "../../taskpane";
import {DocumentModel} from "../../../utils/models/document.model";
import {useDossierService, useInsertionWordService} from "../../../utils/services/http.service";

const useStyles = makeStyles({
    page: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        rowGap: "12px",
    },
    champ: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        rowGap: "4px",
    },
    label: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
    },
    controle: {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
    },
    liste: {
        width: "100%",
        maxWidth: "100%",
        maxHeight: "220px",
        overflowX: "hidden",
        overflowY: "auto",
        boxSizing: "border-box",
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor: tokens.colorNeutralStroke1,
        borderRightColor: tokens.colorNeutralStroke1,
        borderBottomColor: tokens.colorNeutralStroke1,
        borderLeftColor: tokens.colorNeutralStroke1,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    option: {
        display: "block",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderBottomWidth: "1px",
        borderBottomStyle: "solid",
        borderBottomColor: tokens.colorNeutralStroke2,
        backgroundColor: "transparent",
        cursor: "pointer",
        fontSize: tokens.fontSizeBase300,
        fontFamily: "inherit",
        color: tokens.colorNeutralForeground1,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    optionActive: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
    },
    vide: {
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "10px",
        paddingRight: "10px",
        color: tokens.colorNeutralForeground3,
        whiteSpace: "normal",
    },
    arbre: {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        maxHeight: "240px",
        overflow: "auto",
        boxSizing: "border-box",
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
        borderTopColor: tokens.colorNeutralStroke1,
        borderRightColor: tokens.colorNeutralStroke1,
        borderBottomColor: tokens.colorNeutralStroke1,
        borderLeftColor: tokens.colorNeutralStroke1,
        borderRadius: tokens.borderRadiusMedium,
    },
    bouton: {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: "auto",
        whiteSpace: "normal",
        justifyContent: "center",
        columnGap: "8px",
    },
    libelleBouton: {
        minWidth: 0,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
    },
});

export interface ChampsInsererProps {
    document: DocumentModel;
}

enum RubriquesDossier {
    Info = "Info",
    Client = "Client",
    Confrere = "Confrere",
    Tribunal = "Tribunal",
    Postulant = "Postulant",
    Expert = "Expert",
    Mandataire = "Mandataire",
    Huissier = "Huissier",
    Divers = "Divers",
}

const ChampsInserer: React.FC<ChampsInsererProps> = (props: ChampsInsererProps) => {
    const styles = useStyles();
    const dossierService = useDossierService();
    const insertionWordService = useInsertionWordService();

    const rubriquesDossier = [
        {key: RubriquesDossier.Info, text: "Informations dossier"},
        {key: RubriquesDossier.Client, text: "Client(s)"},
        {key: RubriquesDossier.Confrere, text: "Partie(s) Adverse(s)"},
        {key: RubriquesDossier.Tribunal, text: "Tribunaux"},
        {key: RubriquesDossier.Postulant, text: "Postulant"},
        {key: RubriquesDossier.Expert, text: "Expert(s)"},
        {key: RubriquesDossier.Mandataire, text: "Mandataire(s)"},
        {key: RubriquesDossier.Huissier, text: "Huissier(s)"},
        {key: RubriquesDossier.Divers, text: "Diver(s)"},
    ];

    const [referentielChamps, setReferentielChamps] = useState<Record<string, { Champ: string; Libelle: string }[]>>({});
    const [dossiers, setDossiers] = useState([]);

    const [optionChampsInserer, setOptionChampsInserer] = useState('');
    const [optionsChampsInserer, setOptionsChampsInserer] = useState([]);

    const [textApercu, setApercuValue] = useState("");

    const [selectedDossierId, setSelectedDossierId] = useState(null);
    const [listeOuverte, setListeOuverte] = useState(false);
    const [filtre, setFiltre] = useState<string | null>(null);
    const [nomSelectionne, setNomSelectionne] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const nomSelectionneRef = useRef("");
    const selectionEnCours = useRef(false);

    function memoriserNom(nom: string) {
        nomSelectionneRef.current = nom;
        setNomSelectionne(nom);
    }

    const [rubrique, setRubrique] = React.useState(RubriquesDossier.Info);
    const onChangeRubrique: SelectProps["onChange"] = (_event, data) => {
        setRubrique(data.value as RubriquesDossier);
        chargerChampsInsererListe(data.value as RubriquesDossier);
    };

    const onChangeOptionChamp: SelectProps["onChange"] = (_event, data) => {
        setOptionChampsInserer(data.value);
    }

    function chargerReferentielChamps() {
        insertionWordService.champsInsererWord().then((data) => {
            setReferentielChamps(data);
        });
    }

    async function chargerChampsInsererListe(rubrique: RubriquesDossier, dossierId?: number) {
        const id = dossierId ?? selectedDossierId;
        if (!id) {
            return;
        }
        let datas = await insertionWordService.valeursInsererWord(id, rubrique);
        setOptionsChampsInserer(datas.listeForJson ?? []);
        if (rubrique !== RubriquesDossier.Info && datas.listeForJson?.length > 0) {
            setOptionChampsInserer(datas.listeForJson[0].Id);
        }
    }

    useEffect(() => {
        dossierService.donneListeDossiers().then((data) => {
            setDossiers(data.listeForJson ?? []);
        });

        chargerReferentielChamps();

        if (props.document?.DossierId) {
            setSelectedDossierId(props.document.DossierId);
            if (props.document.NomDossier) {
                setSearchValue(props.document.NomDossier);
                memoriserNom(props.document.NomDossier);
            }
            chargerChampsInsererListe(RubriquesDossier.Info, props.document.DossierId);
        }
    }, []);

    function lireValeur(objet: any, cle: string) {
        if (!objet || !cle) {
            return "";
        }
        const camel = cle.charAt(0).toLowerCase() + cle.slice(1);
        const pascal = cle.charAt(0).toUpperCase() + cle.slice(1);
        return objet[cle] ?? objet[camel] ?? objet[pascal] ?? "";
    }

    function champsDeLaRubrique(cleRubrique: string) {
        const referentiel = referentielChamps ?? {};
        if (Array.isArray(referentiel[cleRubrique])) {
            return referentiel[cleRubrique];
        }
        const cle = Object.keys(referentiel).find((nom) => nom.toLowerCase() === cleRubrique.toLowerCase());
        return cle && Array.isArray(referentiel[cle]) ? referentiel[cle] : [];
    }

    function setApercu(champ): any {
        let apercu = '';
        if (rubrique === RubriquesDossier.Info) {
            let dossier = optionsChampsInserer[0];
            apercu = lireValeur(dossier, champ);
        } else {
            let contact = optionsChampsInserer.find(contact => contact.Id === optionChampsInserer);
            apercu = lireValeur(contact, champ);
        }
        setApercuValue(apercu);
    }

    async function ajouterChamp(): Promise<any> {
        await insertText(textApercu);
    }


    const texteFiltre = filtre?.trim().toLowerCase() ?? "";
    const dossiersAffiches = texteFiltre
        ? dossiers.filter((item) => (item.Nom ?? "").toLowerCase().includes(texteFiltre))
        : dossiers;

    async function selectionnerDossier(dossier) {
        const idDossier = Number(dossier.IdDossier);
        memoriserNom(dossier.Nom ?? "");
        setSearchValue(dossier.Nom ?? "");
        setFiltre(null);
        setListeOuverte(false);
        setSelectedDossierId(idDossier);
        setRubrique(RubriquesDossier.Info);

        const datas = await insertionWordService.valeursInsererWord(idDossier, RubriquesDossier.Info);
        setOptionsChampsInserer(datas.listeForJson ?? []);
    }

    const texteChamp = {
        whiteSpace: "normal" as const,
        overflowWrap: "anywhere" as const,
        textOverflow: "unset" as const,
        minWidth: 0,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: "auto",
    };

    return (
        <div className={styles.page}>
            <div className={styles.champ}>
                <label className={styles.label} htmlFor="dossier-recherche">Dossier</label>
                <Input
                    id="dossier-recherche"
                    className={styles.controle}
                    placeholder="Sélectionner un dossier"
                    value={searchValue}
                    autoComplete="off"
                    aria-expanded={listeOuverte}
                    onFocus={() => {
                        setListeOuverte(true);
                        if (searchValue === nomSelectionne) {
                            setFiltre(null);
                        }
                    }}
                    onChange={(_event, data) => {
                        setSearchValue(data.value);
                        setFiltre(data.value);
                        setListeOuverte(true);
                        if (!data.value.trim()) {
                            setSelectedDossierId(null);
                            memoriserNom("");
                        }
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Escape") {
                            setListeOuverte(false);
                            setFiltre(null);
                            setSearchValue(nomSelectionne);
                        }
                    }}
                    onBlur={() => {
                        window.setTimeout(() => {
                            if (selectionEnCours.current) {
                                selectionEnCours.current = false;
                                return;
                            }
                            setListeOuverte(false);
                            setFiltre(null);
                            setSearchValue(nomSelectionneRef.current);
                        }, 200);
                    }}
                />
                {listeOuverte && (
                    <div className={styles.liste} role="listbox" aria-label="Dossiers">
                        {dossiersAffiches.length === 0 && (
                            <div className={styles.vide}>Aucun dossier</div>
                        )}
                        {dossiersAffiches.map((item) => (
                            <button
                                type="button"
                                role="option"
                                key={item.IdDossier}
                                className={String(item.IdDossier) === String(selectedDossierId) ? `${styles.option} ${styles.optionActive}` : styles.option}
                                aria-selected={String(item.IdDossier) === String(selectedDossierId)}
                                onMouseDown={(event) => {
                                    selectionEnCours.current = true;
                                    event.preventDefault();
                                }}
                                onClick={() => {
                                    selectionEnCours.current = false;
                                    selectionnerDossier(item);
                                }}
                            >
                                {item.Nom}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {selectedDossierId > 0 && <div className={styles.page}>
                <div className={styles.champ}>
                    <label className={styles.label}>Rubrique du dossier</label>
                    <Select className={styles.controle} onChange={onChangeRubrique} value={rubrique}>
                        {rubriquesDossier.map((rubriqueDossier) => (
                            <option key={rubriqueDossier.key} value={rubriqueDossier.key}>{rubriqueDossier.text}</option>
                        ))}
                    </Select>
                </div>
                {rubrique !== RubriquesDossier.Info && <div className={styles.champ}>
                    <label className={styles.label}>Champs à insérer</label>
                    {optionsChampsInserer?.length > 0 && <Select className={styles.controle} onChange={onChangeOptionChamp}>
                        {optionsChampsInserer.map((option) => (
                            <option key={option.Id} value={option.Id}>{option.Libelle}</option>
                        ))}
                    </Select>}
                </div>}
                <div className={styles.arbre}>
                    {champsDeLaRubrique(rubrique).length > 0 && (
                        <Tree aria-label="Champs disponibles">
                            {champsDeLaRubrique(rubrique).map((champ) => (
                                <TreeItem itemType="leaf" key={champ.Champ}>
                                    <TreeItemLayout
                                        main={{style: texteChamp}}
                                        onClick={() => setApercu(champ.Champ)}
                                    >
                                        {champ.Libelle}
                                    </TreeItemLayout>
                                </TreeItem>
                            ))}
                        </Tree>
                    )}
                </div>
                <Field className={styles.controle} label="Aperçu">
                    <Textarea className={styles.controle} value={textApercu} onChange={(e) => setApercuValue(e.target.value)}/>
                </Field>
                <Button className={styles.bouton} appearance="primary" onClick={ajouterChamp}>
                    <CalligraphyPenCheckmark20Regular/>
                    <span className={styles.libelleBouton}>Insérer</span>
                </Button>
            </div>}
        </div>
    );
};

export default ChampsInserer;
